import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '../utils/stores/authStore';
import { userApi } from '../api/client';
import { 
  LogOut, User, Shield, Settings, Users, 
  Database, BarChart, FileText, Lock 
} from 'lucide-react';

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, fetchUser } = useAuthStore();
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      setTimeout(() => setSuccessMessage(''), 5000);
    }

    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      await fetchUser();
    } catch (error) {
      console.error('Failed to load user data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const modules = [
    {
      name: 'User Management',
      icon: Users,
      description: 'Manage users, roles, and permissions',
      tier: 'basic',
      permission: 'users.read',
      color: 'blue',
    },
    {
      name: 'Analytics',
      icon: BarChart,
      description: 'View detailed analytics and reports',
      tier: 'premium',
      permission: 'analytics.read',
      color: 'green',
    },
    {
      name: 'Database',
      icon: Database,
      description: 'Access and manage database records',
      tier: 'enterprise',
      permission: 'database.read',
      color: 'purple',
    },
    {
      name: 'Reports',
      icon: FileText,
      description: 'Generate and export reports',
      tier: 'premium',
      permission: 'reports.read',
      color: 'orange',
    },
    {
      name: 'Security',
      icon: Shield,
      description: 'Security settings and audit logs',
      tier: 'enterprise',
      permission: 'security.read',
      color: 'red',
    },
    {
      name: 'Settings',
      icon: Settings,
      description: 'Application and account settings',
      tier: 'free',
      permission: 'settings.read',
      color: 'gray',
    },
  ];

  const tierHierarchy = {
    free: 0,
    basic: 1,
    premium: 2,
    enterprise: 3,
  };

  const canAccessModule = (module) => {
    if (!user) return false;
    
    const userTierLevel = tierHierarchy[user.tier] || 0;
    const moduleTierLevel = tierHierarchy[module.tier] || 0;
    
    const hasTier = userTierLevel >= moduleTierLevel;
    const hasPermission = user.permissions?.includes(module.permission) || false;
    
    return hasTier && hasPermission;
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      purple: 'bg-purple-100 text-purple-600',
      orange: 'bg-orange-100 text-orange-600',
      red: 'bg-red-100 text-red-600',
      gray: 'bg-gray-100 text-gray-600',
    };
    return colors[color] || colors.gray;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-sm text-gray-600">
                Welcome back, {user?.first_name} {user?.last_name}
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Success Message */}
      {successMessage && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-600">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* User Info Card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-primary-600" />
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900">
                {user?.first_name} {user?.last_name}
              </h2>
              <p className="text-gray-600">{user?.email}</p>
              {user?.organization && (
                <p className="text-sm text-gray-500 mt-1">
                  {user.organization} {user.division && `- ${user.division}`}
                </p>
              )}
              <div className="flex items-center space-x-4 mt-3">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                  {user?.tier?.toUpperCase()} Tier
                </span>
                {user?.security_type === 'mfa_totp' && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <Shield className="w-3 h-3 mr-1" />
                    MFA Enabled
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Roles and Permissions */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Access</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Roles</h4>
              <div className="flex flex-wrap gap-2">
                {user?.roles && user.roles.length > 0 ? (
                  user.roles.map((role, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium"
                    >
                      {role}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-gray-500">No roles assigned</span>
                )}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Permissions</h4>
              <div className="flex flex-wrap gap-2">
                {user?.permissions && user.permissions.length > 0 ? (
                  user.permissions.slice(0, 5).map((permission, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium"
                    >
                      {permission}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-gray-500">No permissions</span>
                )}
                {user?.permissions && user.permissions.length > 5 && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                    +{user.permissions.length - 5} more
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Modules Grid */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Modules</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((module, index) => {
              const hasAccess = canAccessModule(module);
              const Icon = module.icon;

              return (
                <div
                  key={index}
                  className={`bg-white rounded-lg shadow-md p-6 transition ${
                    hasAccess
                      ? 'hover:shadow-lg cursor-pointer'
                      : 'opacity-50 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`p-3 rounded-lg ${getColorClasses(module.color)}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-gray-900">{module.name}</h4>
                        {!hasAccess && <Lock className="w-4 h-4 text-gray-400" />}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{module.description}</p>
                      <div className="mt-3">
                        <span className="text-xs text-gray-500 uppercase">
                          Requires: {module.tier}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Upgrade CTA */}
        {user?.tier !== 'enterprise' && (
          <div className="mt-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg shadow-lg p-8 text-white">
            <h3 className="text-2xl font-bold mb-2">Unlock More Features</h3>
            <p className="mb-4">
              Upgrade your account to access premium modules and advanced features.
            </p>
            <button className="bg-white text-primary-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition">
              Upgrade Now
            </button>
          </div>
        )}
      </main>
    </div>
  );
}